<template>
  <q-layout ref="layout" view="lHr LpR fFf">
    <!-- Header -->
    <q-layout-header>
      <subscription-status v-if="activeTeam" :team="activeTeam"/>

      <q-toolbar class="justify-between" color="white" text-color="grey-9">
        <div v-if="!hasValidTeam" class="col-auto">
          <router-link :to="{ name: 'new-team' }">
            <div class="left-drawer-logo">
              <img src="~/assets/logo.svg"/>
            </div>
          </router-link>
        </div>

        <div class="col row items-center">
          <q-btn
            v-if="hasValidTeam"
            class="col-auto lt-lg on-left"
            flat
            round
            dense
            icon="menu"
            @click="showLeft = !showLeft"
          />

          <search-input v-show="hasValidTeam" class="col"/>
        </div>

        <div class="col-auto q-mr-lg">
          <quick-create/>
        </div>

        <div class="col-auto row items-center justify-end">
          <user-section/>
        </div>
      </q-toolbar>
    </q-layout-header>

    <!-- Left Side Panel -->
    <q-layout-drawer v-if="hasValidTeam" v-model="showLeft" side="left" :width="250">
      <div class="column no-wrap full-height">
        <router-link :to="{ name: 'dashboard', params: { teamId: teamId } }">
          <div class="left-drawer-logo">
            <img src="~/assets/logo.svg"/>
          </div>
        </router-link>

        <div class="column col-grow no-wrap justify-between">
          <q-list no-border link>
            <q-list-header>{{ $t('menu') }}</q-list-header>

            <q-item item :to="{ name: 'dashboard', params: { teamId } }">
              <q-item-side icon="home"/>
              <q-item-main :label="$t('dashboard')"/>
            </q-item>

            <q-item item :to="{ name: 'invoices', params: { teamId } }">
              <q-item-side icon="receipt"/>
              <q-item-main :label="$tc('invoice', 2)"/>
            </q-item>

            <q-item item :to="{ name: 'quotes', params: { teamId } }">
              <q-item-side icon="local_offer"/>
              <q-item-main :label="$tc('quote', 2)"/>
            </q-item>

            <collapsible-item :active="this.$route.meta && this.$route.meta.expenses">
              <template slot="main">
                <q-item item :to="{ name: 'expenses-dashboard', params: { teamId } }">
                  <q-item-side icon="description"/>
                  <q-item-main :label="$tc('expense', 2)"/>
                </q-item>
              </template>

              <template slot="expanded">
                <q-item item exact :to="{ name: 'expenses-dashboard', params: { teamId } }">
                  <q-item-main :label="$t('overview')"/>
                </q-item>

                <q-item item exact :to="{ name: 'expenses-list', params: { teamId } }">
                  <q-item-main :label="$t('captured_expenses')"/>
                </q-item>

                <q-item item exact :to="{ name: 'capture-expenses', params: { teamId } }">
                  <q-item-main :label="$t('capture_expenses_heading')"/>
                </q-item>
              </template>

              <template slot="popover">
                <q-item item v-close-overlay exact :to="{ name: 'expenses-dashboard', params: { teamId } }">
                  <q-item-main :label="$t('overview')"/>
                </q-item>

                <q-item item v-close-overlay exact :to="{ name: 'expenses-list', params: { teamId } }">
                  <q-item-main :label="$t('captured_expenses')"/>
                </q-item>

                <q-item item v-close-overlay exact :to="{ name: 'capture-expenses', params: { teamId } }">
                  <q-item-main :label="$t('capture_expenses_heading')"/>
                </q-item>
              </template>
            </collapsible-item>

            <q-item item :to="{ name: 'customers', params: { teamId } }">
              <q-item-side icon="contacts"/>
              <q-item-main :label="$tc('business_partner', 2)"/>
            </q-item>

            <q-item item :to="{ name: 'items', params: { teamId } }">
              <q-item-side icon="local_mall"/>
              <q-item-main :label="$tc('item', 2)"/>
            </q-item>

            <time-entries-navigation-item/>

            <q-item item :to="{ name: 'projects', params: { teamId } }">
              <q-item-side icon="folder"/>
              <q-item-main :label="$tc('project', 2)"/>
            </q-item>

            <q-item item :to="{ name: 'finances', params: { teamId } }">
              <q-item-side icon="euro"/>
              <q-item-main :label="$tc('finance', 2)"/>
            </q-item>
          </q-list>

          <div class="q-pa-lg">
            <support-btn/>

            <div class="row">
              <small class="q-mr-lg">
                <div class="q-pt-md">
                  <a
                    class="legal-link"
                    target="_blank"
                    :href="imprintLink"
                  >
                    {{ $t('imprint')}}
                  </a>
                </div>
              </small>

              <small>
                <div class="q-pt-md">
                  <a
                    class="legal-link"
                    target="_blank"
                    :href="privacyPolicyLink"
                  >
                    {{ $t('privacy_policy')}}
                  </a>
                </div>
              </small>
            </div>
          </div>
        </div>
      </div>
    </q-layout-drawer>

    <!-- sub-routes get injected here: -->
    <q-page-container>
      <router-view/>
    </q-page-container>

  </q-layout>
</template>

<script>
  import CollapsibleItem from 'components/Misc/CollapsibleItem.vue'
  import QuickCreate from 'components/Toolbar/QuickCreate.vue'
  import SearchInput from 'components/Toolbar/SearchInput.vue'
  import SubscriptionStatus from 'components/Toolbar/SubscriptionStatus.vue'
  import SupportBtn from 'components/Misc/SupportBtn.vue'
  import TimeEntriesNavigationItem from 'components/Misc/TimeEntriesNavigationItem.vue'
  import UserSection from 'components/Toolbar/UserSection.vue'
  import { general } from 'src/config'
  import { mapGetters } from 'vuex'

  export default {
    data () {
      return {
        showLeft: true
      }
    },

    meta () {
      return {
        title: this.activeTeam ? [this.activeTeam.name, this.$t('product_name')].join(' - ') : this.$t('product_name')
      }
    },

    computed: {
      ...mapGetters([
        'user',
        'activeTeam'
      ]),

      hasValidTeam () {
        return this.teamId && this.isSubscribedOrOnTrial
      },

      teamId () {
        return this.activeTeam ? this.activeTeam.id : null
      },

      isSubscribedOrOnTrial () {
        return this.activeTeam ? this.activeTeam.isSubscribedOrOnTrial() : false
      },

      imprintLink () {
        return general.imprint_link
      },

      privacyPolicyLink () {
        return general.privacy_policy_link
      }
    },

    components: {
      CollapsibleItem,
      QuickCreate,
      SupportBtn,
      SearchInput,
      SubscriptionStatus,
      UserSection,
      TimeEntriesNavigationItem
    }
  }
</script>

<style lang="stylus">
  .legal-link
    text-decoration: none;
</style>
